/**
 * Український календар для полів дати — замість нативного пікера браузера.
 *
 * ЧОМУ ВЛАСНИЙ, А НЕ `lang`. Chromium бере мову власного календаря з мови
 * ІНТЕРФЕЙСУ браузера, а не з `lang` документа чи елемента. Тому на машині з
 * російським Chrome поля `<input type="date">` відкривали російський календар,
 * і жодна розмітка цього не змінює. Єдиний спосіб гарантувати українську —
 * мати свій пікер.
 *
 * ОДИН ШАР НА ВСЮ ПРОГРАМУ, не компонент на кожне поле. `UaCalendarLayer`
 * монтується раз в App і перехоплює клік по будь-якому `input[type=date]` чи
 * `[type=month]`: гасить нативний пікер і відкриває свій поруч із полем. Так
 * усі секції (Диспетчерська, Прогноз, Ціни, Головна, експорт-модалки, Активи,
 * Умови продажу) отримують українську дату без правок у ~20 місцях, і нові
 * поля теж будуть українськими самі собою.
 *
 * Клавіатуру не забираємо: Tab у поле й набір цифр працюють як раніше — ми
 * гасимо лише мишу та Alt+↓, якими відкривається нативний календар.
 *
 * ПІДПИС МІСЯЦЯ теж локалізований браузером (`type=month` показує назву
 * місяця), тож для місяця в полі лишається нативний текст. Там, де місяць
 * показуємо користувачу, використовуй `MonthField` — він малює підпис сам.
 */
import i18n from '@/i18n';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'lucide-react';

/* ── українські назви ──────────────────────────────────────────────────── */

/** Називний — для заголовка календаря й підпису поля місяця. */
export const UA_MONTHS = () => [
  i18n.t('ak_home_january'), i18n.t('ak_home_february'), i18n.t('ak_home_march'), i18n.t('ak_home_april'), i18n.t('ak_home_may'), i18n.t('ak_home_june'),
  i18n.t('ak_home_july'), i18n.t('ak_home_august'), i18n.t('ak_home_september'), i18n.t('ak_home_october'), i18n.t('ak_home_november'), i18n.t('ak_home_december'),
];
/** Скорочення — для сітки вибору місяця, щоб 12 клітинок не розповзались. */
const UA_MONTHS_SHORT = () => [
  i18n.t('ak_home_jan'), i18n.t('ak_home_feb'), i18n.t('ak_home_mar'), i18n.t('ak_home_apr'), i18n.t('ak_home_may_2'), i18n.t('ak_home_jun'),
  i18n.t('ak_home_jul'), i18n.t('ak_home_aug'), i18n.t('ak_home_sep'), i18n.t('ak_home_oct'), i18n.t('ak_home_nov'), i18n.t('ak_home_dec'),
];
/** Тиждень з понеділка — як в Україні, а не з неділі. */
const UA_WEEKDAYS = () => [i18n.t('ak_home_mon'), i18n.t('ak_home_tue'), i18n.t('ak_home_wed'), i18n.t('ak_home_thu'), i18n.t('ak_home_fri'), i18n.t('ak_home_sat'), i18n.t('ak_home_sun')];

/** Підпис місяця українською з 'YYYY-MM'. */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return UA_MONTHS()[m - 1] ? `${UA_MONTHS()[m - 1]} ${y}` : ym;
}

/* ── дати як рядки ─────────────────────────────────────────────────────────
   Працюємо з 'YYYY-MM-DD' і 'YYYY-MM' як з рядками, а Date беремо лише для
   арифметики календаря і завжди в локальному часі: `new Date('2026-10-01')`
   парситься як UTC і на сході від Гринвіча дає попередню добу. */

const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** Понеділок як 0, неділя як 6. */
const weekdayIndex = (date: Date) => (date.getDay() + 6) % 7;

const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate();

/* ── модель відкритого пікера ──────────────────────────────────────────── */

type Mode = 'date' | 'month';

interface Target {
  el: HTMLInputElement;
  mode: Mode;
  value: string;
  min?: string;
  max?: string;
  rect: DOMRect;
}

/**
 * Запис значення в нативний input так, щоб React побачив зміну.
 *
 * Пряме `el.value = …` React ігнорує: його onChange слухає подію, а не
 * присвоєння, і до того ж React кешує попереднє значення на вузлі. Тому
 * пишемо через сетер прототипу (він оновлює і кеш) і самі відправляємо
 * `input` — саме з нього React робить onChange.
 */
function writeValue(el: HTMLInputElement, next: string) {
  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype, 'value',
  )?.set;
  if (setter) setter.call(el, next);
  else el.value = next;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}

/* ── оформлення ───────────────────────────────────────────────────────────
   Панель — не вкладена плитка: §4 забороняє «плитку на плитці», тож це
   суцільна поверхня з лінією й тінню, без скла та без другого бордера
   всередині. */

const PANEL: React.CSSProperties = {
  position: 'fixed',
  zIndex: 1000,
  width: 268,
  padding: 12,
  borderRadius: 12,
  background: 'var(--surface-solid)',
  border: '1px solid var(--line-strong)',
  boxShadow: 'var(--shadow)',
};

const NAV: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 8,
  background: 'transparent', border: '1px solid var(--line)',
  color: 'var(--text-muted)', fontSize: 13, lineHeight: 1, cursor: 'pointer',
};

const cellStyle = (opts: {
  selected: boolean; today: boolean; dim: boolean; disabled: boolean;
}): React.CSSProperties => ({
  height: 30,
  borderRadius: 8,
  border: opts.today && !opts.selected ? '1px solid var(--line-strong)' : '1px solid transparent',
  background: opts.selected ? 'var(--accent-soft)' : 'transparent',
  color: opts.disabled ? 'var(--text-dim)'
    : opts.selected ? 'var(--text)'
      : opts.dim ? 'var(--text-dim)' : 'var(--text-muted)',
  fontSize: 12.5,
  fontWeight: opts.selected ? 700 : 500,
  cursor: opts.disabled ? 'default' : 'pointer',
  opacity: opts.disabled ? 0.45 : 1,
});

/* ── сітка днів ───────────────────────────────────────────────────────── */

function DayGrid({ view, value, min, max, onPick }: {
  view: { y: number; m: number };
  value: string;
  min?: string;
  max?: string;
  onPick: (iso: string) => void;
}) {
  const first = new Date(view.y, view.m - 1, 1);
  const lead = weekdayIndex(first);
  const total = daysInMonth(view.y, view.m);
  const prevTotal = daysInMonth(view.m === 1 ? view.y - 1 : view.y, view.m === 1 ? 12 : view.m - 1);
  const today = isoOf(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());

  // Шість рядків завжди: інакше панель стрибала б у висоті між місяцями.
  const cells: Array<{ iso: string; label: number; dim: boolean }> = [];
  for (let i = lead - 1; i >= 0; i -= 1) {
    const y = view.m === 1 ? view.y - 1 : view.y;
    const m = view.m === 1 ? 12 : view.m - 1;
    const d = prevTotal - i;
    cells.push({ iso: isoOf(y, m, d), label: d, dim: true });
  }
  for (let d = 1; d <= total; d += 1) {
    cells.push({ iso: isoOf(view.y, view.m, d), label: d, dim: false });
  }
  let tail = 1;
  while (cells.length < 42) {
    const y = view.m === 12 ? view.y + 1 : view.y;
    const m = view.m === 12 ? 1 : view.m + 1;
    cells.push({ iso: isoOf(y, m, tail), label: tail, dim: true });
    tail += 1;
  }

  return (
    <>
      <div className="grid grid-cols-7" style={{ gap: 2, marginBottom: 4 }}>
        {UA_WEEKDAYS().map((w) => (
          <div key={w} style={{
            textAlign: 'center', fontSize: 10.5, fontWeight: 700,
            letterSpacing: '.04em', color: 'var(--text-dim)',
          }}>
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7" style={{ gap: 2 }}>
        {cells.map((c) => {
          const disabled = (!!min && c.iso < min) || (!!max && c.iso > max);
          return (
            <button
              key={c.iso}
              type="button"
              disabled={disabled}
              onClick={() => onPick(c.iso)}
              className="tabular-nums"
              style={cellStyle({
                selected: c.iso === value, today: c.iso === today, dim: c.dim, disabled,
              })}
            >
              {c.label}
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ── сітка місяців ────────────────────────────────────────────────────── */

function MonthGrid({ year, value, min, max, onPick }: {
  year: number;
  value: string;
  min?: string;
  max?: string;
  onPick: (ym: string) => void;
}) {
  const nowYm = `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}`;
  return (
    <div className="grid grid-cols-3" style={{ gap: 4 }}>
      {UA_MONTHS_SHORT().map((label, i) => {
        const ym = `${year}-${pad(i + 1)}`;
        const disabled = (!!min && ym < min.slice(0, 7)) || (!!max && ym > max.slice(0, 7));
        return (
          <button
            key={ym}
            type="button"
            disabled={disabled}
            onClick={() => onPick(ym)}
            style={{
              ...cellStyle({
                selected: ym === value.slice(0, 7), today: ym === nowYm, dim: false, disabled,
              }),
              height: 34,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ── панель ───────────────────────────────────────────────────────────── */

function Panel({ mode, value, min, max, rect, onPick, onClose }: {
  mode: Mode;
  value: string;
  min?: string;
  max?: string;
  rect: DOMRect;
  onPick: (v: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Вид відкриваємо на обраному значенні, а без нього — на поточному місяці.
  const base = value && /^\d{4}-\d{2}/.test(value) ? value : isoOf(
    new Date().getFullYear(), new Date().getMonth() + 1, 1,
  );
  const [view, setView] = useState({
    y: Number(base.slice(0, 4)),
    m: Number(base.slice(5, 7)),
  });

  // Позиція: під полем, а якщо знизу не влазить — над ним; по горизонталі
  // притискаємо до вікна, щоб панель не вилізла за край.
  const [pos, setPos] = useState<{ top: number; left: number }>({
    top: rect.bottom + 6, left: rect.left,
  });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    const top = rect.bottom + 6 + h > window.innerHeight - 8
      ? Math.max(8, rect.top - 6 - h)
      : rect.bottom + 6;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - w - 8);
    setPos({ top, left });
  }, [rect, view, mode]);

  // Закриття: клік поза панеллю, Escape, прокрутка або зміна розміру вікна —
  // після них прив'язка до поля вже неактуальна.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  const shiftMonth = (delta: number) => setView((v) => {
    const d = new Date(v.y, v.m - 1 + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() + 1 };
  });

  const title = mode === 'month' ? String(view.y) : `${UA_MONTHS()[view.m - 1]} ${view.y}`;

  return createPortal(
    <div
      ref={ref}
      style={{ ...PANEL, top: pos.top, left: pos.left }}
      className="font-display"
      role="dialog"
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <button
          type="button"
          style={NAV}
          onClick={() => (mode === 'month' ? setView((v) => ({ ...v, y: v.y - 1 })) : shiftMonth(-1))}
          title={mode === 'month' ? i18n.t('ak_home_previous_year') : i18n.t('ak_home_previous_month')}
        >
          ‹
        </button>
        <div style={{
          fontSize: 12.5, fontWeight: 700, color: 'var(--text)',
          letterSpacing: '.02em',
        }}>
          {title}
        </div>
        <button
          type="button"
          style={NAV}
          onClick={() => (mode === 'month' ? setView((v) => ({ ...v, y: v.y + 1 })) : shiftMonth(1))}
          title={mode === 'month' ? i18n.t('ak_home_next_year') : i18n.t('ak_home_next_month')}
        >
          ›
        </button>
      </div>

      {mode === 'month'
        ? <MonthGrid year={view.y} value={value} min={min} max={max} onPick={onPick} />
        : <DayGrid view={view} value={value} min={min} max={max} onPick={onPick} />}

      {mode === 'date' && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--line)' }}>
          <button
            type="button"
            onClick={() => {
              const n = new Date();
              onPick(isoOf(n.getFullYear(), n.getMonth() + 1, n.getDate()));
            }}
            style={{
              width: '100%', height: 28, borderRadius: 8,
              background: 'transparent', border: '1px solid var(--line)',
              color: 'var(--text-muted)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {i18n.t('ak_home_today')}
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}

/* ── глобальний шар ───────────────────────────────────────────────────── */

const isPickable = (el: EventTarget | null): HTMLInputElement | null => {
  if (!(el instanceof HTMLElement)) return null;
  const input = el.closest('input');
  if (!input) return null;
  const t = input.getAttribute('type');
  if (t !== 'date' && t !== 'month') return null;
  if (input.disabled || input.readOnly) return null;
  // Поля `MonthField` малюють підпис самі й відкривають пікер власним кліком.
  if (input.dataset.uaCalendar === 'off') return null;
  return input;
};

export function UaCalendarLayer() {
  const [target, setTarget] = useState<Target | null>(null);
  const close = useCallback(() => setTarget(null), []);

  useEffect(() => {
    // Перехоплюємо на фазі захоплення й гасимо дефолт: саме дефолтна дія
    // mousedown по значку відкриває нативний календар.
    const onDown = (e: MouseEvent) => {
      const input = isPickable(e.target);
      if (!input) return;
      e.preventDefault();
      const mode: Mode = input.getAttribute('type') === 'month' ? 'month' : 'date';
      setTarget({
        el: input,
        mode,
        value: input.value,
        min: input.min || undefined,
        max: input.max || undefined,
        rect: input.getBoundingClientRect(),
      });
    };
    // Alt+↓ (і F4) — другий шлях до нативного пікера з клавіатури. Набір
    // цифр у полі лишаємо недоторканим.
    const onKey = (e: KeyboardEvent) => {
      const input = isPickable(e.target);
      if (!input) return;
      if ((e.altKey && e.key === 'ArrowDown') || e.key === 'F4') {
        e.preventDefault();
        const mode: Mode = input.getAttribute('type') === 'month' ? 'month' : 'date';
        setTarget({
          el: input, mode, value: input.value,
          min: input.min || undefined, max: input.max || undefined,
          rect: input.getBoundingClientRect(),
        });
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, []);

  if (!target) return null;

  return (
    <Panel
      mode={target.mode}
      value={target.value}
      min={target.min}
      max={target.max}
      rect={target.rect}
      onClose={close}
      onPick={(v) => {
        writeValue(target.el, v);
        close();
      }}
    />
  );
}

/* ── поле місяця з українським підписом ───────────────────────────────── */

/**
 * Поле вибору місяця з підписом «Жовтень 2026».
 *
 * Нативний `type=month` показує назву місяця мовою інтерфейсу браузера, тож
 * підпис малюємо самі. Геометрія — контрол §6: 32 / radius 8 / обводка
 * `--line-strong`, щоб поле стояло в один ряд із кнопками навігації.
 */
export function MonthField({
  value, min, max, onChange, width = 150, title, style, icon = true,
}: {
  value: string;
  min?: string;
  max?: string;
  onChange: (ym: string) => void;
  width?: number | string;
  title?: string;
  /** Накладається на базову геометрію: у хедері контрол 36/10 з тінню (§6). */
  style?: React.CSSProperties;
  /** Вимикай, коли поле вже стоїть у готовій рамці зі своїм значком. */
  icon?: boolean;
}) {
  const btn = useRef<HTMLButtonElement | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  return (
    <>
      <button
        ref={btn}
        type="button"
        title={title}
        onClick={() => setRect(btn.current?.getBoundingClientRect() ?? null)}
        className="flex items-center"
        style={{
          gap: 8, height: 32, width, padding: '0 10px', borderRadius: 8, flexShrink: 0,
          background: 'transparent', border: '1px solid var(--line-strong)',
          color: 'var(--text)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
          ...style,
        }}
      >
        {icon && <Calendar size={14} strokeWidth={2} style={{ color: 'var(--text-dim)' }} />}
        <span>{monthLabel(value)}</span>
      </button>
      {rect && (
        <Panel
          mode="month"
          value={value}
          min={min}
          max={max}
          rect={rect}
          onClose={() => setRect(null)}
          onPick={(v) => { onChange(v); setRect(null); }}
        />
      )}
    </>
  );
}
